import { useEffect, useState } from "react";
import { Box, Button, Grid, Modal, TextField, Typography } from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import BreadcrumbBar from "../../../layout/BreadcrumbBar";
import { EditarTitulo, FlujoMemorias } from "../../../services/PpafeServices";
import Swal from "sweetalert2";
import axios from "axios";
import MyEditor from "../../../services/EditorTexto";
import { puedeActualizar, puedeBaja, puedeCrear } from "../../../utils/permisosFlujo";
import { flujoEnum } from "../../../types/enumTypes";
import { FlujoMemoriasAF } from "../../../services/PpafeservicesAF";



const VerificacionMemoriasAF = () => {
    const { id } = useParams<{ id: string }>();
    const [titulo, setTitulo] = useState<string>("");
    const [comentarios, setComentarios] = useState<string>("");
    const BASEURL = `${process.env.REACT_APP_APPLICATION_ENDPOINT}/aportaciones/federales/memorias/`;
    const navigate = useNavigate();
    const [estatus, setEstatus] = useState<flujoEnum>(flujoEnum.oculto); // Nuevo estado para el estatus
    const [loading, setLoading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [formula, setFormula] = useState("");
    const [error, setError] = useState<string | null>(null);
    const token = localStorage.getItem('jwtToken');
    const [file, setFile] = useState<File | null>(null);
    const [openModal, setOpenModal] = useState(false);
    const [responseData, setResponseData] = useState<any[]>([]);
    const [selectedDocId, setSelectedDocId] = useState<any>(null);
    const isEditing = !!selectedDocId;

    const obtenerSiguienteEstatus = (estatus: "OCULTO" | "DEVUELTO" | "REVISION" | "VALIDACION" | "PUBLICADO" | ""): string | null => {
        const estados: Record<string, string | null> = {
            "OCULTO": "A Revisión",
            "DEVUELTO": "A Revisión",
            "REVISION": "A Validación",
            "VALIDACION": "Publicar",
            "PUBLICADO": null // No mostrar el botón si es PUBLICADO
        };

        return estados[estatus] ?? null; // Devuelve el estado o null si no existe
    };

    const handleDescarga = async (id: string) => {
        try {
            const url = `${process.env.REACT_APP_APPLICATION_ENDPOINT}/documentos/descarga/admin/af_doc_memoria/${id}`;

            const response = await axios.get(url, {
                responseType: 'blob',
                headers: {
                    Authorization: token,
                },
            });

            const blob = new Blob([response.data]);
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;

            // Intenta obtener el nombre del archivo del header Content-Disposition
            const disposition = response.headers['content-disposition'];
            let fileName = 'archivo_descargado';
            if (disposition && disposition.includes('filename=')) {
                fileName = disposition.split('filename=')[1].replace(/"/g, '');
            }

            a.download = fileName;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        } catch (error) {
            console.error('Error al descargar el archivo:', error);
            alert('Hubo un problema al descargar el archivo.');
        }
    };

    // Columnas del DataGrid
    const columns: GridColDef[] = [
        { field: "titulo", headerName: "Título", flex: 1 },
        { field: "comentarios", headerName: "Comentario", flex: 1 },
        {
            field: "fecha_alta",
            headerName: "Fecha Alta",
            flex: 1,
            valueFormatter: (params: any) => {
                const date = new Date(params.value);
                return date.toLocaleDateString("es-ES", { day: "2-digit", month: "2-digit", year: "numeric" });
            }
        },

        {
            field: "estatus",
            headerName: "Estatus",
            flex: 1,
            renderCell: (params) => {
                return params.value === "OCULTO" ? "BAJA" : params.value;
            }
        },

        {
            field: "acciones",
            headerName: "Acciones",
            minWidth: 250, // Ajusta según el espacio que requieran tus botones
            flex: 1,
            sortable: false,
            renderCell: (params: any) => {
                const { id, estatus } = params.row;
                const isDisabled = estatus?.toLowerCase() === "oculto";

                return (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
                        {puedeActualizar(estatus) && (
                            <button onClick={() => actualizarEstatus(id, estatus, false)}>
                                {obtenerSiguienteEstatus(estatus)}
                            </button>
                        )}
                        {puedeBaja() && (
                            <button
                                onClick={() => bajaDoc(id, estatus, "OCULTO")}
                                disabled={isDisabled}
                            >
                                Eliminar
                            </button>
                        )}
                        <button onClick={() => handleDescarga(id)}>Descargar</button>
                        <button onClick={() => handleEditar(params.row)}>Editar</button>
                    </div>
                );
            }
        }
    ];

    const handleAlta = () => {
        setSelectedDocId(null);
        setTitulo("");
        setComentarios("");
        setFile(null);
        setOpenModal(true);
    };
    const handleEditar = (row: any) => {
        setSelectedDocId(row.id);
        setTitulo(row.titulo || "");
        setComentarios(row.comentarios || "");
        setOpenModal(true)
    };

    const handleClose = () => {
        setOpenModal(false);
        setTitulo("");
        setComentarios("");
        setFile(null);
    };

    const handleTituloChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setTitulo(e.target.value);
    };

    const handleComentarioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setComentarios(e.target.value);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFile(e.target.files[0]);
        }
    };

    const handleUpdate = async () => {
        if (!titulo || !comentarios) {
            Swal.fire({
                icon: "error",
                title: "Completa todos los campos"
            });
            return;
        }

        setLoading(true);

        try {
            const response = await axios.put(
                `${BASEURL}actualiza/${selectedDocId}/doc`,
                {
                    titulo,
                    comentarios
                },
                {
                    headers: {
                        Authorization: localStorage.getItem("jwtToken") || "",
                    },
                }
            );

            if (response.status === 200) {
                Swal.fire({
                    icon: "success",
                    title: "Documento actualizado correctamente"
                });

                handleClose();
                window.location.reload();
            }
        } catch (error: any) {
            Swal.fire({
                icon: "error",
                title: "Error",
                text: error?.response?.data?.message || error.message,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async () => {
        if (!titulo || !comentarios || !file) {
            Swal.fire({
                icon: "error",
                title: "Por favor, completa todos los campos."
            });
            return;
        }

        setLoading(true);

        const formData = new FormData();
        formData.append("file", file);
        formData.append("titulo", titulo);
        formData.append("comentarios", comentarios);
        try {

            const response = await axios.post(
                BASEURL + id + "/documentos",
                formData,
                {
                    headers: {
                        Authorization: localStorage.getItem("jwtToken") || "",
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.status === 200) {
                Swal.fire({
                    icon: "info",
                    title: "Documento cargado con éxito.",
                    text: response.data.message || "Hubo un problema al guardar la información. Intenta nuevamente.",
                });
                window.location.reload();
                handleClose(); // Cerrar el modal
            } else {
                Swal.fire({
                    icon: "error",
                    title: "Error al cargar el documento",
                    text: response.data.message || "Hubo un problema al guardar la información. Intenta nuevamente.",
                });
            }
        } catch (error: any) {
            Swal.fire({
                icon: "error",
                title: "Error al cargar el documento",
                text: error?.message || "Hubo un problema al guardar la información. Intenta nuevamente.",
            });
        } finally {
            setLoading(false);
        }
    };
    const flujoDetalle = (accion: "avanzar" | "devolver") => {
        const tipo = "aportaciones";
        const origen = "federales";

        FlujoMemoriasAF(tipo, origen, setLoading, setResponseData, setError, estatus, id || "", accion);
    };


    const editarFormula = () => {
        setEditMode(true)
        setFormula(memoria?.formula || "")
    };

    const guardarFormula = async () => {
        try {
            const response = await axios.put(BASEURL + 'actualiza/formula/' + id, {
                formula: formula
            }, {
                headers: {
                    'Authorization': `${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (response.status === 200) {
                window.location.reload();
                return response.data;
            } else {
                console.error("Error al actualizar fórmula:", response.data);
                Swal.fire({
                    icon: "error",
                    title: "¡No se logró guardar la Información!",
                    text: response.data.message || "Hubo un problema al guardar la información. Intenta nuevamente.",
                });
            }
        } catch (error) {
            console.error("Error en la solicitud:", error);
            // Si hubo un error en la solicitud, mostramos el Toast de error
            Swal.fire({
                icon: "error",
                title: "¡Error al conectar con el servidor!",
                text: "Por favor, intenta nuevamente más tarde.",
            });
        }
    };

    const [memoria, setMemoria] = useState<any>({});
    const [documentos, setDocumentos] = useState([]);


    const ObtenerDatosDetalle = async () => {
        try {
            const response = await axios.get(BASEURL + 'detalle/' + id, {
                headers: {
                    'Authorization': `${token}`,
                    'Content-Type': 'application/json'
                }
            });
            if (response.data.success) {
                setEstatus(response.data.data.estatus)
                setMemoria(response.data.data);
                setDocumentos(response.data.data.documentos);
            } else {
                setError("Memoria no encontrada");
            }
        } catch (err) {
            setError("Error al obtener la memoria");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        ObtenerDatosDetalle();

    }, []); // Dependencias vacías para ejecutarse solo al montar el componente
    const getEstatusCorrecto = (estatus: string) => {
        const estatusLower = estatus.toLowerCase(); // Convertimos todo a minúsculas
        switch (estatusLower) {
            case "revision":
                return "Revisión";
            case "validacion":
                return "Validación";
            case "publicado":
                return "Publicado";
            case "devuelto":
                return "Devuelto";
            default:
                return estatus.toUpperCase(); // En caso de que el estatus no esté en los casos anteriores
        }
    };

    const actualizarEstatus = async (id: string, estatus: string, esMemoria: boolean) => {
        try {
            const nextEstatus: Record<string, string | null> = {
                "OCULTO": "REVISION",
                "DEVUELTO": "REVISION",
                "REVISION": "VALIDACION",
                "VALIDACION": "PUBLICADO",
                "PUBLICADO": null,
            };

            const siguiente = nextEstatus[estatus];

            if (!siguiente) {
                console.warn("No hay siguiente estatus disponible.");
                return;
            }

            // Mostrar SweetAlert de confirmación
            const confirmacion = await Swal.fire({
                title: "¿Estás seguro?",
                text: `Esta acción cambiará el estatus.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Sí, actualizar",
                confirmButtonColor: "#bda889",
                cancelButtonColor: "#15212f",
                cancelButtonText: "Cancelar",
            });

            if (!confirmacion.isConfirmed) {
                return;
            }

            const url = `${BASEURL}${esMemoria ? "actualiza/" : "actualizadoc/"}${id}`;

            const response = await axios.put(
                url,
                { estatus: siguiente },
                {
                    headers: {
                        Authorization: `${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                await Swal.fire({
                    title: "Actualización exitosa",
                    text: `El estatus se ha actualizado .`,
                    icon: "success",
                    confirmButtonText: "Aceptar",
                    confirmButtonColor: "#15212f",
                });

                window.location.reload();
                return response.data;
            } else {
                console.error("Error al actualizar estatus:", response.data);
                await Swal.fire({
                    title: "Error",
                    text: "No se pudo actualizar el estatus. Inténtalo de nuevo.",
                    icon: "error",
                    confirmButtonText: "Aceptar",
                    confirmButtonColor: "#15212f",
                });
            }
        } catch (error) {
            console.error("Error en la solicitud de actualización:", error);
            await Swal.fire({
                title: "Error",
                text: "Ocurrió un problema al procesar la solicitud.",
                icon: "error",
                confirmButtonText: "Aceptar",
                confirmButtonColor: "#15212f",
            });
        }
    };


    const bajaDoc = async (
        id: string,
        estatus: string,
        estatusDestino?: string
    ) => {
        try {
            const nextEstatus: Record<string, string | null> = {
                "OCULTO": "REVISION",
                "DEVUELTO": "REVISION",
                "REVISION": "VALIDACION",
                "VALIDACION": "PUBLICADO",
                "PUBLICADO": null,
            };

            const siguiente = estatusDestino ?? nextEstatus[estatus];

            if (!siguiente) {
                console.warn("No hay siguiente estatus disponible.");
                return;
            }

            // Mostrar SweetAlert de confirmación
            const confirmacion = await Swal.fire({
                title: "¿Estás seguro?",
                text: `Esta acción cambiará el estatus del documento.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonText: "Sí, actualizar",
                confirmButtonColor: "#bda889",
                cancelButtonColor: "#15212f",
                cancelButtonText: "Cancelar",
            });

            if (!confirmacion.isConfirmed) {
                return;
            }

            const url = `${BASEURL}actualizadoc/${id}`;

            const response = await axios.put(
                url,
                { estatus: siguiente },
                {
                    headers: {
                        Authorization: `${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.status === 200) {
                await Swal.fire({
                    title: "Actualización exitosa",
                    text: `El estatus ha sido actualizado.`,
                    icon: "success",
                    confirmButtonText: "Aceptar",
                    confirmButtonColor: "#15212f",
                });

                window.location.reload();
                return response.data;
            } else {
                console.error("Error al actualizar estatus:", response.data);

                await Swal.fire({
                    title: "Error",
                    text: "No se pudo actualizar el estatus. Inténtalo de nuevo.",
                    icon: "error",
                    confirmButtonText: "Aceptar",
                    confirmButtonColor: "#15212f",
                });
            }
        } catch (error) {
            console.error("Error en la solicitud de actualización:", error);

            await Swal.fire({
                title: "Error",
                text: "Hubo un problema con la solicitud. Inténtalo más tarde.",
                icon: "error",
                confirmButtonText: "Aceptar",
                confirmButtonColor: "#15212f",
            });
        }
    };

    const TITULO = estatus ? `${getEstatusCorrecto(estatus)} de Memorias de Calculo` : "Validación de Memorias de Calculo";

    return (

        <Grid container sx={{}}>
            <Grid item mb={2} width={"100%"}>
                <BreadcrumbBar
                    links={[
                        { texto: "Inicio", link: "/admin" },
                        { texto: "Memorias", link: "/admin/aportaciones/federales/memoriasdecalculo" },
                    ]}
                    Titulo={TITULO}
                />
            </Grid>
            <Grid container spacing={2} mb={2}>
                {/* Título principal */}
                <Grid item xs={12}>
                    <Typography
                        variant="h5"
                        component="div"
                        sx={{
                            fontWeight: "bold",
                            display: "flex",
                            justifyContent: "flex-start",
                        }}
                    >
                        {estatus ? `${getEstatusCorrecto(estatus)} Memoria de cálculo` : "Memoria de cálculo"}
                    </Typography>
                </Grid>



                {/* Título de memoria */}
                <Grid item xs={12}>
                    <Typography>
                        <b>{memoria?.titulo}</b>
                    </Typography>
                </Grid>

                {/* Editor o fórmula */}
                <Grid item xs={12}>
                    {editMode ? (
                        <MyEditor content={formula} setContent={setFormula} />
                    ) : (
                        <div dangerouslySetInnerHTML={{ __html: memoria?.formula || "" }} />
                    )}
                </Grid>

                {/* Botón responsivo */}
                <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent={{ xs: "center", md: "flex-end" }}
                    mt={2}
                >
                    {editMode ? (
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                width: { xs: "100%", md: "9rem" },
                                textTransform: "capitalize",
                            }}
                            disabled={
                                estatus === "PUBLICADO" ||
                                estatus === "OCULTO" ||
                                estatus === "DEVUELTO"
                            }
                            onClick={() => guardarFormula()}
                        >
                            Guardar
                        </Button>
                    ) : (
                        memoria?.estatus === "REVISION" && (
                            <Button
                                variant="contained"
                                color="primary"
                                sx={{
                                    width: { xs: "100%", md: "9rem" },
                                    textTransform: "capitalize",
                                }}
                                disabled={
                                    estatus === "PUBLICADO" ||
                                    estatus === "OCULTO" ||
                                    estatus === "DEVUELTO"
                                }
                                onClick={() => editarFormula()}
                            >
                                Editar Fórmula
                            </Button>
                        )
                    )}
                </Grid>
            </Grid>


            <Grid
                container
                spacing={2}
                sx={{ mt: 2 }}
                justifyContent={{ xs: "center", md: "flex-end" }}
            >
                <Grid
                    item
                    xs={12}
                    md="auto"
                    display="flex"
                    justifyContent={{ xs: "center", md: "flex-end" }}
                >
                    <Button
                        variant="contained"
                        sx={{
                            width: { xs: "100%", md: "8rem" },
                            textTransform: "capitalize",
                        }}
                        onClick={() => navigate("/admin/aportaciones/federales/memoriasdecalculo")}
                        style={{ backgroundColor: "#C1A25C" }}
                    >
                        Cancelar
                    </Button>
                </Grid>

                {puedeActualizar(estatus) && (
                    <Grid
                        item
                        xs={12}
                        md="auto"
                        display="flex"
                        justifyContent={{ xs: "center", md: "flex-end" }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                width: { xs: "100%", md: "8rem" },
                                textTransform: "capitalize",
                            }}
                            disabled={
                                estatus === "OCULTO" ||
                                estatus === "DEVUELTO"
                            }
                            onClick={() => flujoDetalle("devolver")}
                        >
                            Devolver
                        </Button>
                    </Grid>
                )}

                {!["PUBLICADO"].includes(estatus) && puedeActualizar(estatus) && (
                    <Grid
                        item
                        xs={12}
                        md="auto"
                        display="flex"
                        justifyContent={{ xs: "center", md: "flex-end" }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            sx={{
                                width: { xs: "100%", md: "8rem" },
                                textTransform: "capitalize",
                            }}
                            disabled={estatus === "PUBLICADO"}
                            onClick={() => actualizarEstatus(id!, estatus, true)}
                        >
                            {obtenerSiguienteEstatus(estatus)}
                        </Button>
                    </Grid>
                )}
            </Grid>


            <Grid container>
                <Typography>
                    <b>Documentos</b>
                </Typography>
                <Box sx={{ width: "100%", overflowX: "auto" }}>
                    <Box sx={{ minWidth: 700, height: 400 }}>
                        <DataGrid rows={documentos} columns={columns} pageSize={25} />
                    </Box>
                </Box>
                <Grid
                    container
                    spacing={2}
                    justifyContent={{ xs: "center", md: "flex-end" }}
                    mt={2}
                >
                    {puedeCrear() && (
                        <Grid item xs={12} md="auto" display="flex" justifyContent={{ xs: "center", md: "flex-end" }}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleAlta}
                                sx={{
                                    width: { xs: "100%", md: "auto" },
                                    textTransform: "capitalize",
                                }}
                            >
                                Alta documento
                            </Button>
                        </Grid>
                    )}
                </Grid>

            </Grid>
            <Modal open={openModal} onClose={handleClose}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        width: 400,
                        backgroundColor: "white",
                        padding: 2,
                        borderRadius: 1,
                    }}
                >
                    <Typography variant="h6">
                        {isEditing ? "Editar Documento" : "Agregar Documento"}
                    </Typography>


                    <TextField
                        label="Título del Documento"
                        fullWidth
                        value={titulo}
                        onChange={handleTituloChange}
                        margin="normal"
                    />

                    <TextField
                        label="Comentario"
                        fullWidth
                        value={comentarios}
                        onChange={handleComentarioChange}
                        margin="normal"
                    />

                    {!selectedDocId && (
                        <input
                            type="file"
                            accept=".csv, .xls, .xlsx"
                            onChange={handleFileChange}
                            style={{ marginBottom: "10px" }}
                        />
                    )}

                    <Button
                        variant="contained"
                        onClick={isEditing ? handleUpdate : handleSubmit}
                    >
                        {isEditing ? "Actualizar" : "Subir"}
                    </Button>
                </Box>
            </Modal>
        </Grid >
    );
};

export default VerificacionMemoriasAF;
